import { Link } from "react-router-dom";
import { CalendarDays, MapPin } from "lucide-react";
import { useApi, img } from "@/lib/api";
import { formatRange } from "@/lib/image";
import { Seo } from "@/components/site/Seo";
import { PageHeader, Reveal } from "@/components/site/Reveal";
import { RetreatHostForm } from "@/components/site/RetreatHostForm";
import { GalleryStrip } from "@/components/site/GalleryStrip";
import { useLang } from "@/lib/i18n";

export const RetreatCard = ({ r }) => {
  const { t, L, lang, to } = useLang();
  const href = to(`/retreats/${r.slug}`);
  return (
    <article className="card-lift flex h-full flex-col overflow-hidden rounded-2xl border border-clay bg-white" data-testid={`retreat-card-${r.slug}`}>
      <Link to={href} className="img-zoom block aspect-[4/3] overflow-hidden"><img src={img(r.image)} alt={L(r, "title")} loading="lazy" className="h-full w-full object-cover" /></Link>
      <div className="flex flex-1 flex-col p-6">
        <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-sage"><CalendarDays className="h-3.5 w-3.5" /> {formatRange(r.start_date, r.end_date, lang)}</p>
        <h3 className="mt-2 font-display text-2xl font-black text-ink"><Link to={href} className="hover:text-forest">{L(r, "title")}</Link></h3>
        <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-moss"><MapPin className="h-3.5 w-3.5" /> {L(r, "venue")}</p>
        <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-moss">{L(r, "description")}</p>
        <Link to={href} className="btn-outline mt-auto self-start !px-5 !py-2" style={{ marginTop: "1.5rem" }} data-testid={`retreat-card-details-${r.slug}`}>{t("retreat.details")}</Link>
      </div>
    </article>
  );
};

export default function Retreats() {
  const { t } = useLang();
  const { data, loading } = useApi("/retreats", []);
  return (
    <>
      <Seo title={t("retreat.seoTitle")} description={t("retreat.seoDesc")} />
      <PageHeader testId="retreats-header" eyebrow={t("retreat.eyebrow")} title={t("retreat.title")}>
        {t("retreat.intro")}
        <div className="mt-6"><a href="#host" className="btn-primary" data-testid="retreats-host-cta">{t("retreat.hostCta")}</a></div>
      </PageHeader>
      <section className="container-x pb-20" data-testid="retreats-list">
        {!loading && data.length === 0 && <p className="rounded-2xl border border-clay bg-linen p-8 font-semibold text-moss" data-testid="retreats-empty">{t("retreat.empty")}</p>}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((r, i) => <Reveal key={r.slug} delay={(i % 3) * 0.08}><RetreatCard r={r} /></Reveal>)}
        </div>
      </section>
      <GalleryStrip className="container-x pb-20" testId="retreats-gallery" />
      <section id="host" className="container-x scroll-mt-24 pb-28" data-testid="retreats-host">
        <div className="grid gap-10 rounded-3xl bg-linen p-8 sm:p-12 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <p className="eyebrow">{t("retreat.hostEyebrow")}</p>
            <h2 className="mt-3 text-3xl font-black tracking-tight text-forest sm:text-4xl">{t("retreat.hostTitle")}</h2>
            <p className="mt-4 font-medium leading-relaxed text-moss">{t("retreat.hostText")}</p>
          </div>
          <div className="lg:col-span-3"><RetreatHostForm /></div>
        </div>
      </section>
    </>
  );
}
